<template>
    <div class="space-y-4">
        <div class="flex flex-wrap items-end justify-between gap-2 border-b border-gray-200">
            <nav class="flex items-center gap-1 -mb-px">
                <button
                    v-for="v in INTERVENTION_VIEWS"
                    :key="v.key"
                    type="button"
                    class="whitespace-nowrap rounded-t-md border-b-2 px-3 pb-2 pt-1.5 text-sm font-medium transition-colors"
                    :class="view === v.key ? 'border-primary-500 bg-gray-100 text-gray-900' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'"
                    @click="view = v.key"
                >
                    {{ v.label }}
                </button>
            </nav>
            <UInput
                v-model="search"
                type="text"
                placeholder="Search by patient or #..."
                class="mb-2 w-72"
                icon="i-ph-magnifying-glass-duotone"
            />
        </div>

        <div class="flex flex-wrap items-center gap-2">
            <USelectMenu
                v-if="statusOptions.length > 1"
                v-model="filters.status"
                trailing
                :class="filters.status ? 'w-52' : 'w-36'"
                :leading-icon="filters.status ? undefined : 'i-ph-funnel-duotone'"
                :ui="selectUi"
                :options="statusOptions"
                value-attribute="value"
                option-attribute="name"
            >
                <template #label>
                    <template v-if="filters.status">
                        <span class="text-gray-400">Status:</span>
                        <span class="truncate font-medium">{{ statusOptions.find(o => o.value === filters.status)?.name }}</span>
                    </template>
                    <span v-else class="text-gray-400">Status</span>
                </template>
                <template #trailing v-if="filters.status">
                    <button @click.stop="filters.status = undefined" type="button" class="flex items-center justify-center">
                        <UIcon name="i-ph-x-bold" class="size-3" />
                    </button>
                </template>
            </USelectMenu>
            <USelectMenu
                v-model="filters.reason"
                trailing
                searchable
                :class="filters.reason ? 'w-56' : 'w-36'"
                :leading-icon="filters.reason ? undefined : 'i-ph-stethoscope-duotone'"
                :ui="selectUi"
                :options="reasonOptions"
                value-attribute="value"
                option-attribute="name"
            >
                <template #label>
                    <template v-if="filters.reason">
                        <span class="text-gray-400">Reason:</span>
                        <span class="truncate font-medium">{{ reasonOptions.find(o => o.value === filters.reason)?.name }}</span>
                    </template>
                    <span v-else class="text-gray-400">Reason</span>
                </template>
                <template #trailing v-if="filters.reason">
                    <button @click.stop="filters.reason = undefined" type="button" class="flex items-center justify-center">
                        <UIcon name="i-ph-x-bold" class="size-3" />
                    </button>
                </template>
            </USelectMenu>
            <USelectMenu
                v-if="view !== 'open'"
                v-model="filters.disposition"
                trailing
                :class="filters.disposition ? 'w-60' : 'w-40'"
                :leading-icon="filters.disposition ? undefined : 'i-ph-sign-out-duotone'"
                :ui="selectUi"
                :options="dispositionOptions"
                value-attribute="value"
                option-attribute="name"
            >
                <template #label>
                    <template v-if="filters.disposition">
                        <span class="text-gray-400">Disposition:</span>
                        <span class="truncate font-medium">{{ dispositionOptions.find(o => o.value === filters.disposition)?.name }}</span>
                    </template>
                    <span v-else class="text-gray-400">Disposition</span>
                </template>
                <template #trailing v-if="filters.disposition">
                    <button @click.stop="filters.disposition = undefined" type="button" class="flex items-center justify-center">
                        <UIcon name="i-ph-x-bold" class="size-3" />
                    </button>
                </template>
            </USelectMenu>
            <USelectMenu
                v-if="areaOptions.length"
                v-model="filters.area"
                trailing
                :class="filters.area ? 'w-48' : 'w-32'"
                :leading-icon="filters.area ? undefined : 'i-ph-map-pin-duotone'"
                :ui="selectUi"
                :options="areaOptions"
                value-attribute="value"
                option-attribute="name"
            >
                <template #label>
                    <template v-if="filters.area">
                        <span class="text-gray-400">Area:</span>
                        <span class="truncate font-medium">{{ filters.area }}</span>
                    </template>
                    <span v-else class="text-gray-400">Area</span>
                </template>
                <template #trailing v-if="filters.area">
                    <button @click.stop="filters.area = undefined" type="button" class="flex items-center justify-center">
                        <UIcon name="i-ph-x-bold" class="size-3" />
                    </button>
                </template>
            </USelectMenu>
            <UPopover :popper="{ placement: 'bottom-start' }">
                <UButton color="white" :icon="hasDateRange ? undefined : 'i-ph-calendar-duotone'" :ui="{ font: 'font-normal' }">
                    <template v-if="hasDateRange">
                        <span class="text-gray-400">Arrived:</span>
                        <span class="font-medium">{{ dateRangeLabel }}</span>
                    </template>
                    <span v-else class="text-gray-400">Arrived</span>
                    <template #trailing v-if="hasDateRange">
                        <span role="button" class="flex items-center justify-center" @click.stop="clearDateRange">
                            <UIcon name="i-ph-x-bold" class="size-3" />
                        </span>
                    </template>
                </UButton>
                <template #panel>
                    <div class="w-64 space-y-2 p-3">
                        <div>
                            <span class="mb-1 block text-xs text-gray-500">From</span>
                            <UInput v-model="filters.from" type="date" :max="filters.to" />
                        </div>
                        <div>
                            <span class="mb-1 block text-xs text-gray-500">To</span>
                            <UInput v-model="filters.to" type="date" :min="filters.from" />
                        </div>
                    </div>
                </template>
            </UPopover>
        </div>

        <UTable
            :rows="rows"
            :columns="visibleColumns"
            :loading="loading"
            :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
            :empty-state="{ icon: 'i-ph-clipboard-text-duotone', label: emptyLabel }"
            :ui="tableUi"
            @select="openIntervention"
        >
            <template #number-data="{ row }">
                <ULink :to="interventionPath(row)" class="whitespace-nowrap font-mono hover:underline">{{ formatInterventionNumber(row.number) }}</ULink>
            </template>
            <template #patient-data="{ row }">
                <div class="leading-5">
                    <p v-if="patientName(row)">{{ patientName(row) }}</p>
                    <p v-else class="text-gray-300">–</p>
                    <span v-if="row.area" class="text-sm">{{ row.area }}</span>
                </div>
            </template>
            <template #reason-data="{ row }">
                <div class="leading-5">
                    <p v-if="row.reason_label">{{ row.reason_label }}</p>
                    <p v-else class="text-gray-300">–</p>
                    <span v-if="row.disposition_label" class="text-sm">{{ row.disposition_label }}</span>
                </div>
            </template>
            <template #arrived-data="{ row }">
                <div class="whitespace-nowrap leading-5">
                    <p>{{ formatArrived(row.arrived_at, todayIso(now)) }}</p>
                    <span v-if="row.status === InterventionStatus.inProgress" class="text-sm">{{ elapsedMinutes(row.arrived_at, now) }} min ago</span>
                    <span v-else-if="row.discharged_at" class="text-sm">Stayed {{ durationMinutes(row) }} min</span>
                </div>
            </template>
            <template #status-data="{ row }">
                <BadgeInterventionStatus :status="row.status" class="whitespace-nowrap" />
            </template>
        </UTable>

        <TablePagination :pagination="pagination" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { watchDebounced } from '@vueuse/core'
import { INTERVENTION_STATUS_META, InterventionStatus, type FieldPost } from '~/types/fieldPost'
import { OptionNamespace } from '~/types/option'
import { elapsedMinutes, todayIso } from '~/utils/fieldPosts'
import {
    INTERVENTION_VIEWS,
    formatInterventionNumber,
    searchInterventionsParams,
    viewStatuses,
    type InterventionFilters,
    type InterventionView,
} from '~/utils/interventionSearch'

interface InterventionRow {
    id: string
    number: number
    post_id: string
    area: string | null
    status: InterventionStatus
    arrived_at: string
    discharged_at: string | null
    reason_id: string | null
    reason_label: string | null
    disposition_id: string | null
    disposition_label: string | null
    patient_first_name: string | null
    patient_last_name: string | null
    total_count: number
}

const props = defineProps<{ post: FieldPost }>()

const client = useSupabaseClient()
const route = useRoute()
const router = useRouter()
const pagination = usePagination()

const loading = ref(true)
const rows = ref<InterventionRow[]>([])
const search = ref('')
const filters = ref<InterventionFilters>({})
const options = ref<{ id: string; namespace: OptionNamespace; label: string }[]>([])
const now = ref(new Date())
let ticker: ReturnType<typeof setInterval> | undefined

const view = computed<InterventionView>({
    get() {
        const key = route.query.view as InterventionView
        return INTERVENTION_VIEWS.some(v => v.key === key) ? key : 'open'
    },
    set(key) {
        router.replace({ query: { ...route.query, view: key === 'open' ? undefined : key } })
    },
})

const columns = [
    { key: 'number', label: '#', class: 'w-px' },
    { key: 'patient', label: 'Patient' },
    { key: 'reason', label: 'Reason', class: 'w-full' },
    { key: 'arrived', label: 'Arrived' },
    { key: 'status', label: 'Status', class: 'w-px' },
]

const visibleColumns = computed(() => view.value === 'open' ? columns.filter(c => c.key !== 'status') : columns)

const tableUi = { th: { padding: 'px-3' }, td: { base: 'whitespace-normal', padding: 'px-3 py-3' }, tr: { base: 'cursor-pointer hover:bg-gray-50' } }
const selectUi = { icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } } }

const statusOptions = computed(() => viewStatuses(view.value).map(s => ({ value: s, name: INTERVENTION_STATUS_META[s].label })))
const reasonOptions = computed(() => optionsFor(OptionNamespace.interventionReason))
const dispositionOptions = computed(() => optionsFor(OptionNamespace.interventionDisposition))
const areaOptions = computed(() => (props.post.areas ?? []).map(a => ({ value: a, name: a })))

const hasDateRange = computed(() => !!(filters.value.from || filters.value.to))
const dateRangeLabel = computed(() => {
    const fmt = (d: string) => dayjs(d).format('MMM D')
    const { from, to } = filters.value
    if (from && to) return from === to ? fmt(from) : `${fmt(from)} – ${fmt(to)}`
    return from ? `from ${fmt(from)}` : `until ${fmt(to!)}`
})

const emptyLabel = computed(() => {
    if (search.value.trim() || Object.values(filters.value).some(Boolean)) return 'No interventions match these filters.'
    return view.value === 'open' ? 'No open interventions.' : view.value === 'closed' ? 'No closed interventions.' : 'No interventions.'
})

function optionsFor(namespace: OptionNamespace) {
    return options.value.filter(o => o.namespace === namespace).map(o => ({ value: o.id, name: o.label }))
}

function clearDateRange() {
    filters.value.from = undefined
    filters.value.to = undefined
}

function formatArrived(iso: string, referenceDay: string) {
    const d = dayjs(iso)
    if (d.format('YYYY-MM-DD') === referenceDay) return d.format('HH:mm')
    return d.format(d.year() === dayjs(referenceDay).year() ? 'MMM D, HH:mm' : 'MMM D, YYYY HH:mm')
}

function durationMinutes(i: InterventionRow) {
    return Math.max(0, dayjs(i.discharged_at).diff(dayjs(i.arrived_at), 'minute'))
}

function interventionPath(i: InterventionRow) {
    return `/field-posts/${props.post.id}/interventions/${i.id}`
}

function openIntervention(i: InterventionRow) {
    navigateTo(interventionPath(i))
}

function patientName(i: InterventionRow) {
    return [i.patient_first_name, i.patient_last_name].filter(Boolean).join(' ')
}

onMounted(() => {
    fetchData()
    fetchOptions()
    ticker = setInterval(() => { now.value = new Date() }, 30_000)
})

onUnmounted(() => clearInterval(ticker))

watch(view, () => {
    filters.value.status = undefined
    if (view.value === 'open') filters.value.disposition = undefined
    refetchFromStart()
})
watchDebounced(search, () => refetchFromStart(), { debounce: 600 })
watch(filters, () => refetchFromStart(), { deep: true })
watch(() => pagination.from, () => fetchData())

function refetchFromStart() {
    if (!pagination.reset()) fetchData()
}

async function fetchOptions() {
    const { data } = await client.from('options')
        .select('id, namespace, label')
        .in('namespace', [OptionNamespace.interventionReason, OptionNamespace.interventionDisposition])
        .eq('is_archived', false)
        .order('label')
    options.value = (data ?? []) as typeof options.value
}

let fetchSeq = 0

async function fetchData() {
    const seq = ++fetchSeq
    loading.value = true
    rows.value = []
    pagination.setTotal(undefined)

    const params = searchInterventionsParams(props.post.id, view.value, filters.value, search.value, pagination)
    const { data } = await client.rpc('search_interventions', params as never)
    if (seq !== fetchSeq) return

    rows.value = (data ?? []) as InterventionRow[]
    pagination.setTotal(rows.value[0]?.total_count ?? 0)
    loading.value = false
}
</script>
